import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../../components/design-system/Card';
import { Button } from '../../components/design-system/Button';
import { clsx } from 'clsx';
import confetti from 'canvas-confetti';

const OnboardingSuccess: React.FC = () => {
  const navigate = useNavigate();
  const [showContent, setShowContent] = useState(false);

  useEffect(() => {
    // Trigger confetti animation
    const duration = 3 * 1000;
    const animationEnd = Date.now() + duration;
    const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 0 };

    const randomInRange = (min: number, max: number) => {
      return Math.random() * (max - min) + min;
    };

    const interval: any = setInterval(() => {
      const timeLeft = animationEnd - Date.now();

      if (timeLeft <= 0) {
        return window.clearInterval ? window.clearInterval(interval) : clearInterval(interval);
      }

      const particleCount = 50 * (timeLeft / duration);
      
      // Confetti from left
      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 }
      });
      
      // Confetti from right
      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 }
      });
    }, 250);

    // Show content after a short delay
    setTimeout(() => setShowContent(true), 500);

    return () => {
      if (window.clearInterval) {
        window.clearInterval(interval);
      } else if (typeof clearInterval !== 'undefined') {
        clearInterval(interval);
      }
    };
  }, []);

  const nextSteps = [
    {
      icon: '🎯',
      title: 'Your First Workout',
      description: 'Start with a personalized workout tailored to your goals',
      action: () => navigate('/workouts/today'),
      buttonText: 'Start Workout'
    },
    {
      icon: '📊',
      title: 'Explore Your Dashboard',
      description: 'Track your progress and see your personalized insights',
      action: () => navigate('/dashboard'),
      buttonText: 'View Dashboard'
    },
    {
      icon: '🤝',
      title: 'Connect with a Coach',
      description: 'Get expert guidance to accelerate your progress',
      action: () => navigate('/coaches'),
      buttonText: 'Find Coaches'
    },
    {
      icon: '📱',
      title: 'Download Mobile App',
      description: 'Take your workouts anywhere with our mobile app',
      action: () => window.open('https://www.motionwiseai.com/download', '_blank'),
      buttonText: 'Get App'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#4B5FDC]/10 to-[#00C896]/10 flex items-center justify-center p-4">
      <div className={clsx(
        'max-w-4xl w-full transition-all duration-1000',
        showContent ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
      )}>
        {/* Success Message */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-[#00C896] rounded-full mb-4 animate-bounce">
            <svg className="w-10 h-10 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Congratulations! 🎉
          </h1>
          
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            You've completed your profile setup. Your personalized fitness journey begins now!
          </p>
        </div>

        {/* AI Message */}
        <Card className="mb-8 bg-gradient-to-r from-[#4B5FDC]/5 to-[#00C896]/5 border-2 border-[#4B5FDC]/20">
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-[#4B5FDC] rounded-full flex items-center justify-center">
                <span className="text-2xl">🤖</span>
              </div>
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-gray-900 mb-2">
                Your AI Coach Says:
              </h3>
              <p className="text-gray-700">
                "Based on your profile, I've created a personalized plan to help you build muscle effectively. 
                Your first workout focuses on compound movements to establish a strong foundation. 
                Remember, consistency is key – aim for at least 3 workouts this week!"
              </p>
            </div>
          </div>
        </Card>

        {/* Next Steps */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">
            What's Next?
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {nextSteps.map((step, index) => (
              <Card
                key={index}
                variant="interactive"
                className={clsx(
                  'animate-slideUp',
                  index === 0 && 'border-2 border-[#4B5FDC] shadow-lg'
                )}
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="flex items-start space-x-4">
                  <div className="text-3xl">{step.icon}</div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900 mb-1">
                      {step.title}
                    </h3>
                    <p className="text-sm text-gray-600 mb-3">
                      {step.description}
                    </p>
                    <Button
                      variant={index === 0 ? 'primary' : 'ghost'}
                      size="sm"
                      onClick={step.action}
                      haptic
                    >
                      {step.buttonText}
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>

        {/* Quick Stats */}
        <Card className="bg-gray-50">
          <h3 className="font-semibold text-gray-900 mb-4 text-center">
            Your Starting Stats
          </h3>
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <div className="text-2xl font-bold text-[#4B5FDC]">0</div>
              <div className="text-sm text-gray-600">Workouts</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-[#00C896]">3</div>
              <div className="text-sm text-gray-600">Weekly Goal</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900">Day 1</div>
              <div className="text-sm text-gray-600">Journey Start</div>
            </div>
          </div>
        </Card>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 mt-8">
          <Button
            variant="primary"
            size="lg"
            onClick={() => navigate('/workouts/today')}
            className="flex-1"
            haptic
            celebrateOnClick
          >
            Start First Workout
          </Button>
          <Button
            variant="ghost"
            size="lg"
            onClick={() => navigate('/dashboard')}
            className="flex-1"
          >
            Go to Dashboard
          </Button>
        </div>

        {/* Progress Tips */}
        <div className="mt-12 text-center text-sm text-gray-500">
          <p>💡 Pro tip: Complete your first workout today to build momentum!</p>
        </div>
      </div>
    </div>
  );
};

export default OnboardingSuccess;